import { useState } from 'react'
import { CircleAlert, Download, RefreshCw } from 'lucide-react'

import { usePageTitle } from 'Layout/DocumentTitle'
import {
  useBillingPaymentMutation,
  useBillingRenewalMutation,
  useBillingSummary,
  useInvoicePdfMutation,
} from 'query/useBilling'
import useCurrentAccount from 'query/useCurrentAccount'
import {
  Alert,
  Button,
  FormCard,
  Icon,
  LoadingIndicator,
  PageContent,
  PageHeader,
} from 'ui'
import Api from 'utils/api'
import {
  accountPayloadFromForm,
  billingSummaryUnavailable,
  clearCardFields,
  formatCurrency,
  formatDate,
  mergeBillingSummary,
  normalizeBillingHistory,
  shouldLoadBillingHistory,
  titleize,
  validationSummaryMessage,
} from 'utils/billing'

import ActivationSummary from './ActivationSummary'
import BillingDashboard from './BillingDashboard'
import PaymentComplete, { getCompletedPaymentDetails, PaymentCompleteHeaderMeta } from './PaymentComplete'
import PaymentForm from './PaymentForm'
import UpgradeOrderSummary from './UpgradeOrderSummary'
import { intervalDescription } from './UpgradePresentation'

const BILLING_DETAILS_UNAVAILABLE_MESSAGE = 'Billing details are unavailable.'

const SummaryList = ({ items }) => (
  <dl className="tw:grid tw:gap-3">
    {items.filter(([, value]) => value !== undefined).map(([label, value]) => (
      <div className="tw:min-w-0" key={label}>
        <dt className="ds-helper">{label}</dt>
        <dd className="tw:mt-1 tw:[overflow-wrap:anywhere] tw:text-base tw:text-foreground">{value ?? 'None'}</dd>
      </div>
    ))}
  </dl>
)

const FallbackAccountInfoCard = ({ account }) => (
  <FormCard className="tw:mb-4" title="Account Information">
    <SummaryList
      items={[
        ['Team', account.name],
        ['Plan', account.plan_name || account.planName],
        ['Period', titleize(account.period)],
        ['Rate', formatCurrency(account.rate, 'USD')],
        ['Expiration', formatDate(account.expiration_date || account.expirationDate)],
        ['Server Editions', `${account.serverInstanceCount || 0} / ${account.serverInstanceLimit || 0}`],
      ]}
    />
  </FormCard>
)

export const BillingPage = () => {
  const [errorMessage, setErrorMessage] = useState('')
  const [notice, setNotice] = useState('')
  const [paymentFormOpen, setPaymentFormOpen] = useState(false)
  const [paymentSubmitting, setPaymentSubmitting] = useState(false)
  const [upgradeComplete, setUpgradeComplete] = useState(false)
  const [validationErrors, setValidationErrors] = useState({})
  const billingQuery = useBillingSummary()
  const paymentMutation = useBillingPaymentMutation()
  const renewalMutation = useBillingRenewalMutation()
  const invoiceMutation = useInvoicePdfMutation()
  const { fallbackAccount, summary } = billingQuery.data
  const currentAccountQuery = useCurrentAccount({
    enabled: Boolean(summary?.upgradeRequired || upgradeComplete),
  })

  const currentAccount = currentAccountQuery.data || {}
  const setCurrentAccount = currentAccountQuery.setData
  const canRenderBillingSummary = summary && !billingSummaryUnavailable(summary)
  const isActivation = Boolean(canRenderBillingSummary && summary.upgradeRequired && !paymentFormOpen && !upgradeComplete)
  const isPaymentForm = Boolean(canRenderBillingSummary && paymentFormOpen && !upgradeComplete)
  const isSubmitting = paymentSubmitting || renewalMutation.isPending
  const isDownloading = invoiceMutation.isPending
  const isLoading = billingQuery.isLoading || (isActivation && currentAccountQuery.isLoading)
  const pageTitle = upgradeComplete
    ? 'Payment Complete'
    : isPaymentForm
      ? summary.upgradeRequired ? 'Payment Details' : 'Update Credit Card'
      : isActivation ? 'Activate Your Subscription' : 'Billing'

  usePageTitle(pageTitle)

  const updateSummary = summaryUpdate => {
    billingQuery.setData(current => ({
      ...current,
      summary: mergeBillingSummary(current?.summary, summaryUpdate),
    }))
  }

  const resetMessages = () => {
    setErrorMessage('')
    setNotice('')
    setValidationErrors({})
  }

  const handlePaymentSubmit = async event => {
    event.preventDefault()
    resetMessages()
    setPaymentSubmitting(true)
    const form = event.currentTarget
    const account = accountPayloadFromForm(new FormData(form))
    const isUpgrade = summary?.upgradeRequired

    try {
      let nextSummary = await paymentMutation.mutateAsync({ account, isUpgrade })
      const summaryAfterPayment = mergeBillingSummary(summary, nextSummary)

      if (shouldLoadBillingHistory(summaryAfterPayment)) {
        try {
          const response = await Api.get('/billing/history')
          nextSummary = { ...nextSummary, history: normalizeBillingHistory(response.history) }
        } catch (_historyError) {
          // The payment succeeded; keep the current history if its refresh is unavailable.
        }
      }

      clearCardFields(form)
      updateSummary(nextSummary)
      setPaymentFormOpen(false)

      if (isUpgrade) {
        const period = nextSummary.subscription?.period

        setCurrentAccount({ ...currentAccount, ...(period ? { period } : {}) })
        setUpgradeComplete(true)
      } else {
        setNotice('Payment method updated')
      }
    } catch (error) {
      const nextValidationErrors = error.validationErrors || {}
      setErrorMessage(validationSummaryMessage(nextValidationErrors) || error.message)
      setValidationErrors(nextValidationErrors)
    } finally {
      setPaymentSubmitting(false)
    }
  }

  const handleSetAutoRenewal = async autoRenewal => {
    resetMessages()

    try {
      const nextSummary = await renewalMutation.mutateAsync(autoRenewal)
      updateSummary(nextSummary)
      setNotice(autoRenewal ? 'Renewal resumed' : 'Renewal cancellation saved')
    } catch (error) {
      setErrorMessage(error.message)
    }
  }

  const handleDownloadInvoice = async item => {
    setErrorMessage('')

    try {
      const pdf = await invoiceMutation.mutateAsync(item.id)
      const bytes = Uint8Array.from(atob(pdf.contentBase64), character => character.charCodeAt(0))
      const url = URL.createObjectURL(new Blob([bytes], { type: pdf.contentType || 'application/pdf' }))
      const link = document.createElement('a')

      link.href = url
      link.download = pdf.filename || `invoice_${item.id}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (error) {
      setErrorMessage(error.message)
    }
  }

  const refreshAction = (
    <Button
      disabled={isLoading || isSubmitting}
      icon={<Icon as={RefreshCw} />}
      onClick={() => {
        resetMessages()
        setPaymentFormOpen(false)
        setUpgradeComplete(false)
        billingQuery.refetch()
      }}
      type="button"
      variant="secondary"
    >
      Refresh
    </Button>
  )

  const amount = formatCurrency(summary?.upgrade?.amount, summary?.upgrade?.currency)
  const trialExpiration = currentAccount.expiration_date || currentAccount.expirationDate
  const trialEnd = trialExpiration ? formatDate(trialExpiration) : null
  const completedInvoice = getCompletedPaymentDetails(summary).invoice
  const headerProps = upgradeComplete ? {
    meta: <PaymentCompleteHeaderMeta account={currentAccount} summary={summary} />,
    subtitle: 'Your Server Edition subscription is active. Download an installer, then follow the install guide to register it.',
    title: 'Payment Complete',
    action: completedInvoice ? (
      <Button
        disabled={isDownloading}
        icon={<Icon as={Download} />}
        onClick={() => handleDownloadInvoice(completedInvoice)}
        size="sm"
        variant="secondary"
      >
        {isDownloading ? 'Downloading…' : 'Download invoice'}
      </Button>
    ) : undefined,
  } : isPaymentForm ? {
    subtitle: summary.upgradeRequired
      ? intervalDescription(amount, summary.upgrade?.interval)
      : 'Update your payment method on file. New charges will use this card.',
    title: summary.upgradeRequired ? 'Payment Details' : 'Update Credit Card',
  } : isActivation ? {
    meta: trialEnd && trialEnd !== 'None' ? `Your trial ends ${trialEnd}` : undefined,
    subtitle: 'Your account is set up. Add a payment method to unlock Server Edition downloads and the Web Management Console.',
    title: 'Activate Your Subscription',
  } : {
    action: refreshAction,
    subtitle: 'Manage your subscription, payment method, and payment history.',
    title: 'Billing',
  }

  if (isLoading && !summary) {
    return (
      <>
        <PageHeader {...headerProps} />
        <PageContent><LoadingIndicator /></PageContent>
      </>
    )
  }

  return (
    <>
      <PageHeader {...headerProps} />
      <PageContent>
        {!canRenderBillingSummary ? (
          <>
            <Alert className="tw:flex tw:items-start tw:gap-2" color="danger" role="alert">
              <Icon as={CircleAlert} className="tw:mt-0.5 tw:shrink-0" />
              <div>
                <strong className="tw:font-semibold">{BILLING_DETAILS_UNAVAILABLE_MESSAGE}</strong>
                {' '}Your subscription is unaffected—try refreshing, or reach out and we can confirm your account status.
              </div>
            </Alert>
            {fallbackAccount && <FallbackAccountInfoCard account={fallbackAccount} />}
          </>
        ) : (
          <>
            {errorMessage && <Alert color="danger">{errorMessage}</Alert>}
            {notice && <Alert color="success">{notice}</Alert>}

            {upgradeComplete ? (
              <PaymentComplete
                account={currentAccount}
              />
            ) : isPaymentForm ? (
              <div className={summary.upgradeRequired
                ? 'tw:grid tw:grid-cols-[minmax(0,2fr)_minmax(300px,1fr)] tw:gap-6 tw:shell:grid-cols-1'
                : undefined}
              >
                <PaymentForm
                  account={summary.account}
                  formOptions={summary.formOptions}
                  isSubmitting={isSubmitting}
                  onCancel={() => {
                    setErrorMessage('')
                    setValidationErrors({})
                    setPaymentFormOpen(false)
                  }}
                  onSubmit={handlePaymentSubmit}
                  upgrade={summary.upgrade}
                  upgradeRequired={summary.upgradeRequired}
                  validationErrors={validationErrors}
                />
                {summary.upgradeRequired && (
                  <UpgradeOrderSummary
                    planName={currentAccount.plan_name || currentAccount.planName}
                    upgrade={summary.upgrade}
                  />
                )}
              </div>
            ) : summary.upgradeRequired ? (
              <ActivationSummary
                account={currentAccount}
                brand={summary.brand}
                onContinue={() => {
                  resetMessages()
                  setPaymentFormOpen(true)
                }}
                upgrade={summary.upgrade}
              />
            ) : (
              <BillingDashboard
                isDownloading={isDownloading}
                isSubmitting={isSubmitting}
                onDownloadInvoice={handleDownloadInvoice}
                onOpenPaymentForm={() => {
                  resetMessages()
                  setPaymentFormOpen(true)
                }}
                onSetAutoRenewal={handleSetAutoRenewal}
                summary={summary}
              />
            )}
          </>
        )}
      </PageContent>
    </>
  )
}

export default BillingPage
